import axios from "axios";
import apiConfig from "../config/api.js";

/**
 * Service d'authentification
 * Gère les requêtes d'authentification et le stockage des tokens
 */
class AuthService {
  constructor() {
    this.baseURL = apiConfig.baseURL;
    // Ne pas stocker le token statiquement - le récupérer dynamiquement
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null;
  }

  /**
   * Configurer les intercepteurs axios
   */
  setupInterceptors() {
    // Intercepteur de requête pour ajouter le token
    axios.interceptors.request.use(
      (config) => {
        // Récupérer le token depuis localStorage à chaque requête
        const currentToken = localStorage.getItem("token");
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
          config.headers["Content-Type"] = "application/json";
          console.log(
            "🔑 Token ajouté à la requête:",
            currentToken.substring(0, 20) + "..."
          );
        } else {
          console.log("❌ Pas de token trouvé pour la requête");
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Intercepteur de réponse pour gérer les erreurs d'authentification
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.logout();
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Connexion d'un utilisateur
   * @param {string} username - Nom d'utilisateur ou email
   * @param {string} password - Mot de passe
   * @returns {Promise<Object>} Données de l'utilisateur et token
   */
  async login(username, password) {
    try {
      const response = await axios.post(`${this.baseURL}/users/login`, {
        username,
        password,
      });

      if (response.data.success) {
        this.setAuthData(response.data.data, response.data.token);
        return response.data;
      }

      throw new Error(response.data.message || "Erreur de connexion");
    } catch (error) {
      throw error;
    }
  }

  /**
   * Inscription d'un nouvel utilisateur
   * @param {Object} userData - Données de l'utilisateur
   * @returns {Promise<Object>} Données de l'utilisateur et token
   */
  async register(userData) {
    try {
      const response = await axios.post(
        `${this.baseURL}/users/register`,
        userData
      );

      if (response.data.success) {
        this.setAuthData(response.data.data, response.data.token);
        return response.data;
      }

      throw new Error(response.data.message || "Erreur d'inscription");
    } catch (error) {
      throw error;
    }
  }

  /**
   * Obtenir le profil de l'utilisateur connecté
   * @returns {Promise<Object>} Profil de l'utilisateur
   */
  async getProfile() {
    try {
      const response = await axios.get(`${this.baseURL}/users/profile`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Mettre à jour le profil de l'utilisateur
   * @param {Object} userData - Nouvelles données de l'utilisateur
   * @returns {Promise<Object>} Profil mis à jour
   */
  async updateProfile(userData) {
    try {
      const response = await axios.put(
        `${this.baseURL}/users/profile`,
        userData
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Déconnexion de l'utilisateur
   */
  logout() {
    this.token = null;
    this.user = null;
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  }

  /**
   * Définir les données d'authentification
   * @param {Object} user - Données de l'utilisateur
   * @param {string} token - Token JWT
   */
  setAuthData(user, token) {
    this.user = user;
    this.token = token;
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("token", token);
  }

  /**
   * Vérifier si l'utilisateur est connecté
   * @returns {boolean} True si connecté
   */
  isAuthenticated() {
    return !!(this.token && this.user);
  }

  /**
   * Obtenir l'utilisateur actuel
   * @returns {Object|null} Données de l'utilisateur
   */
  getCurrentUser() {
    return this.user;
  }

  /**
   * Obtenir le token actuel
   * @returns {string|null} Token JWT
   */
  getToken() {
    return this.token;
  }

  /**
   * Obtenir les recettes de l'utilisateur connecté
   * @returns {Promise<Array>} Liste des recettes de l'utilisateur
   */
  async getMyRecipes() {
    try {
      const response = await axios.get(
        `${this.baseURL}/recipes?my_recipes=true`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Obtenir la liste des utilisateurs (admin uniquement)
   * @returns {Promise<Object>} Liste des utilisateurs
   */
  async getAllUsersAdmin() {
    try {
      const response = await axios.get(`${this.baseURL}/users`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Créer un utilisateur depuis l'admin
   */
  async createUserAdmin(userData) {
    try {
      const response = await axios.post(`${this.baseURL}/users/admin/users`, userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Mettre à jour un utilisateur depuis l'admin
   */
  async updateUserAdmin(userId, userData) {
    try {
      const response = await axios.put(
        `${this.baseURL}/users/admin/users/${userId}`,
        userData
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Supprimer un utilisateur depuis l'admin
   */
  async deleteUserAdmin(userId) {
    try {
      const response = await axios.delete(`${this.baseURL}/users/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Changer le mot de passe d'un utilisateur depuis l'admin
   */
  async resetUserPasswordAdmin(userId, newPassword) {
    try {
      const response = await axios.put(
        `${this.baseURL}/users/admin/users/${userId}/password`,
        { newPassword }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Récupérer les recettes pour l'admin (modération)
   */
  async getRecipesAdmin(params = {}) {
    try {
      const response = await axios.get(`${this.baseURL}/recipes`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Supprimer une recette en tant qu'admin
   */
  async deleteRecipeAdmin(recipeId) {
    try {
      const response = await axios.delete(`${this.baseURL}/recipes/admin/${recipeId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Récupérer les commentaires récents communautaires (admin)
   */
  async getRecentCommentsAdmin(limit = 10) {
    try {
      const response = await axios.get(`${this.baseURL}/comments/recent`, {
        params: { limit },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Créer une recette pour l'utilisateur connecté
   * @param {Object} recipeData - Données de la recette
   * @returns {Promise<Object>} Recette créée
   */
  async createRecipe(recipeData) {
    try {
      const response = await axios.post(`${this.baseURL}/recipes`, recipeData);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

// Créer une instance singleton
const authService = new AuthService();
authService.setupInterceptors();

export default authService;
